// src/services/SupervisorService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { Supervisor, SupervisorNote, Visit, ClientFile } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

type File = ClientFile;

const supervisorCollectionRef = collection(db, "supervisors");
const visitCollectionRef = collection(db, "visits");

export const SupervisorService = {
  async createSupervisor(
    supervisorData: Omit<Supervisor, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newSupervisor: Omit<Supervisor, "id"> = {
      ...supervisorData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const supervisorRef = await addDoc(supervisorCollectionRef, newSupervisor);
    return supervisorRef.id;
  },

  async getSupervisor(supervisorId: string): Promise<Supervisor | null> {
    const supervisorDoc = await getDoc(
      doc(supervisorCollectionRef, supervisorId),
    );
    if (supervisorDoc.exists()) {
      return { id: supervisorDoc.id, ...supervisorDoc.data() } as Supervisor;
    }
    return null;
  },

  async getSupervisorVisits(
    supervisorId: string,
    accountId: string,
  ): Promise<Visit[]> {
    const visitSnapshot = await getDocs(
      query(
        visitCollectionRef,
        where("supervisorId", "==", supervisorId),
        where("accountId", "==", accountId),
      ),
    );
    return visitSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Visit,
    );
  },

  async getSupervisorFiles(supervisorId: string): Promise<File[]> {
    const supervisorFilesSnapshot = await getDocs(
      collection(db, "supervisors", supervisorId, "files"),
    );
    if (supervisorFilesSnapshot.empty) {
      return [];
    }
    return supervisorFilesSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as File,
    );
  },

  async addFileToSupervisor(
    supervisorId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newFile: Omit<File, "id"> = {
      ...fileData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const fileRef = await addDoc(
      collection(db, "supervisors", supervisorId, "files"),
      newFile,
    );
    return fileRef.id;
  },

  async deleteFileFromSupervisor(
    supervisorId: string,
    fileId: string,
  ): Promise<void> {
    const fileRef = doc(
      collection(db, "supervisors", supervisorId, "files"),
      fileId,
    );
    await deleteDoc(fileRef);
  },

  async getAllSupervisors(accountId: string): Promise<Supervisor[]> {
    const supervisorSnapshot = await getDocs(
      query(
        supervisorCollectionRef,
        where("accountId", "==", accountId),
        orderBy("firstName", "asc"),
      ),
    );
    return supervisorSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Supervisor,
    );
  },

  async updateSupervisor(
    supervisorId: string,
    updatedData: Partial<Omit<Supervisor, "id" | "createdAt">>,
  ): Promise<void> {
    const supervisorDocRef = doc(supervisorCollectionRef, supervisorId);
    await updateDoc(supervisorDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteSupervisor(supervisorId: string): Promise<void> {
    const supervisorDocRef = doc(supervisorCollectionRef, supervisorId);
    await deleteDoc(supervisorDocRef);
  },

  // ============================================================================
  // SUPERVISOR NOTES
  // ============================================================================

  async getSupervisorNotes(supervisorId: string): Promise<SupervisorNote[]> {
    const notesSnapshot = await getDocs(
      query(
        collection(db, "supervisors", supervisorId, "notes"),
        orderBy("createdAt", "desc"),
      ),
    );
    return notesSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as SupervisorNote,
    );
  },

  async addNoteToSupervisor(
    supervisorId: string,
    noteData: Omit<SupervisorNote, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newNote: Omit<SupervisorNote, "id"> = {
      ...noteData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const noteRef = await addDoc(
      collection(db, "supervisors", supervisorId, "notes"),
      newNote,
    );
    return noteRef.id;
  },

  async updateSupervisorNote(
    supervisorId: string,
    noteId: string,
    content: string,
  ): Promise<void> {
    const noteRef = doc(
      collection(db, "supervisors", supervisorId, "notes"),
      noteId,
    );
    await updateDoc(noteRef, {
      content,
      updatedAt: Timestamp.now(),
    });
  },

  async deleteNoteFromSupervisor(
    supervisorId: string,
    noteId: string,
  ): Promise<void> {
    const noteRef = doc(
      collection(db, "supervisors", supervisorId, "notes"),
      noteId,
    );
    await deleteDoc(noteRef);
  },
};
